import {
    SchedulePolicyKind,
    toSchedulePolicyKind,
} from "@common/helpers/domains/accounting/schedulePolicyKind";
import { ScheduleTemplateListItem } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { ScheduleTemplateStatus } from "@common/types/domains/accounting/schedule";

export type SchedulesSortKey = "name" | "updatedAt" | "journalEntryCount";

export const DEFAULT_SCHEDULES_SORT: SchedulesSortKey = "name";

/** The least a template needs to be grouped: the hub and the picker share it. */
export interface GroupableScheduleTemplate {
    name: string;
    group: string;
}

export interface ScheduleGroup<
    TTemplate extends GroupableScheduleTemplate = ScheduleTemplateListItem,
> {
    name: string;
    templates: TTemplate[];
}

function compareTemplates(
    leftTemplate: ScheduleTemplateListItem,
    rightTemplate: ScheduleTemplateListItem,
    sortKey: SchedulesSortKey,
): number {
    if (sortKey === "updatedAt") {
        return (
            new Date(rightTemplate.updatedAt).getTime() -
            new Date(leftTemplate.updatedAt).getTime()
        );
    }
    if (sortKey === "journalEntryCount") {
        return rightTemplate.journalEntryCount - leftTemplate.journalEntryCount;
    }
    return leftTemplate.name.localeCompare(rightTemplate.name);
}

export type ScheduleTemplateJournalEntriesFilter = "with" | "without";

export interface ScheduleTemplateFilters {
    templateIds: string[];
    statuses: ScheduleTemplateStatus[];
    journalEntries: ScheduleTemplateJournalEntriesFilter[];
    policyKinds: SchedulePolicyKind[];
    groups: string[];
    entityIds: number[];
}

export const EMPTY_SCHEDULE_TEMPLATE_FILTERS: ScheduleTemplateFilters = {
    templateIds: [],
    statuses: [],
    journalEntries: [],
    policyKinds: [],
    groups: [],
    entityIds: [],
};

export function hasActiveScheduleTemplateFilters(
    filters: ScheduleTemplateFilters,
): boolean {
    return (
        filters.templateIds.length > 0 ||
        filters.statuses.length > 0 ||
        filters.journalEntries.length > 0 ||
        filters.policyKinds.length > 0 ||
        filters.groups.length > 0 ||
        filters.entityIds.length > 0
    );
}

export function collectScheduleTemplateGroups(
    templates: ScheduleTemplateListItem[],
): string[] {
    return [...new Set(templates.map((template) => template.group))].sort(
        (left, right) => left.localeCompare(right),
    );
}

/** An empty filter matches every policy; one without a known kind never matches a set one. */
function matchesPolicyKindFilter(
    template: ScheduleTemplateListItem,
    policyKinds: SchedulePolicyKind[],
): boolean {
    if (policyKinds.length === 0) {
        return true;
    }
    const kind = toSchedulePolicyKind(template);
    return kind !== null && policyKinds.includes(kind);
}

function matchesJournalEntriesFilter(
    template: ScheduleTemplateListItem,
    journalEntries: ScheduleTemplateJournalEntriesFilter[],
): boolean {
    if (journalEntries.length === 0) {
        return true;
    }
    const hasJournalEntries = template.journalEntryCount > 0;
    return journalEntries.some((option) =>
        option === "with" ? hasJournalEntries : !hasJournalEntries,
    );
}

export function compareSchedulesByName(
    leftTemplate: GroupableScheduleTemplate,
    rightTemplate: GroupableScheduleTemplate,
): number {
    return leftTemplate.name.localeCompare(rightTemplate.name);
}

function matchesSearch(
    template: { name: string; description?: string | null },
    normalizedSearch: string,
): boolean {
    if (normalizedSearch === "") {
        return true;
    }
    const description = template.description ?? "";
    return (
        template.name.toLowerCase().includes(normalizedSearch) ||
        description.toLowerCase().includes(normalizedSearch)
    );
}

/** Search only, for pickers that carry none of the hub's filters. */
export function searchScheduleTemplates<
    TTemplate extends { name: string; description?: string | null },
>(templates: TTemplate[], search: string): TTemplate[] {
    const normalizedSearch = search.trim().toLowerCase();
    return templates.filter((template) =>
        matchesSearch(template, normalizedSearch),
    );
}

export function filterScheduleTemplates(options: {
    templates: ScheduleTemplateListItem[];
    search: string;
    filters: ScheduleTemplateFilters;
}): ScheduleTemplateListItem[] {
    const { templates, filters } = options;
    const normalizedSearch = options.search.trim().toLowerCase();
    return templates.filter((template) => {
        if (
            filters.templateIds.length > 0 &&
            !filters.templateIds.includes(template.id)
        ) {
            return false;
        }
        if (
            filters.statuses.length > 0 &&
            !filters.statuses.includes(template.status)
        ) {
            return false;
        }
        if (!matchesJournalEntriesFilter(template, filters.journalEntries)) {
            return false;
        }
        if (!matchesPolicyKindFilter(template, filters.policyKinds)) {
            return false;
        }
        if (
            filters.groups.length > 0 &&
            !filters.groups.includes(template.group)
        ) {
            return false;
        }
        if (
            filters.entityIds.length > 0 &&
            !template.entityIds.some((entityId) =>
                filters.entityIds.includes(entityId),
            )
        ) {
            return false;
        }
        return matchesSearch(template, normalizedSearch);
    });
}

/**
 * Group order is the hub's order (alphabetical today); the picker reuses this
 * so its headers never disagree with the hub.
 */
export function groupScheduleTemplates<
    TTemplate extends GroupableScheduleTemplate,
>(
    templates: TTemplate[],
    compare: (leftTemplate: TTemplate, rightTemplate: TTemplate) => number,
): Array<ScheduleGroup<TTemplate>> {
    const templatesByGroup = new Map<string, TTemplate[]>();
    for (const template of templates) {
        const groupTemplates = templatesByGroup.get(template.group) ?? [];
        groupTemplates.push(template);
        templatesByGroup.set(template.group, groupTemplates);
    }

    const groupNames = [...templatesByGroup.keys()].sort((left, right) =>
        left.localeCompare(right),
    );

    return groupNames.map((groupName) => ({
        name: groupName,
        templates: [...(templatesByGroup.get(groupName) ?? [])].sort(compare),
    }));
}

export function buildScheduleGroups(
    templates: ScheduleTemplateListItem[],
    sortKey: SchedulesSortKey,
): ScheduleGroup[] {
    return groupScheduleTemplates(templates, (leftTemplate, rightTemplate) =>
        compareTemplates(leftTemplate, rightTemplate, sortKey),
    );
}
